import { type VariantProps, cva } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 font-semibold whitespace-nowrap tabular-nums",
  {
    variants: {
      tone: {
        neutral: "bg-surface-muted text-text-secondary",
        success: "bg-success/14 text-success",
        warning: "bg-warning/14 text-warning",
        danger: "bg-danger/14 text-danger",
        highlight: "bg-highlight/12 text-highlight",
        teal: "bg-primary/14 text-primary-text",
      },
      size: {
        md: "rounded-chip px-2 py-[3px] text-[11px]",
        sm: "rounded-tag px-1.5 py-0.5 text-[10px]",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export interface BadgeProps
  extends ComponentProps<"span">, VariantProps<typeof badgeVariants> {}

export function Badge({ tone, size, className, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone, size }), className)} {...props} />;
}

interface CountBadgeProps {
  count: number;
  max?: number;
  /** Lo que lee el lector de pantalla, p. ej. «3 sin ver». */
  label?: string;
  size?: "md" | "sm";
  className?: string;
}

/** El contador neón. Un cero no se dibuja: es ruido. */
export function CountBadge({
  count,
  max = 99,
  label,
  size = "md",
  className,
}: CountBadgeProps) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={label}
      className={cn(
        "bg-highlight text-highlight-foreground inline-flex items-center justify-center font-semibold tabular-nums",
        size === "md"
          ? "rounded-chip h-5 min-w-[22px] px-[7px] text-[11px]"
          : "rounded-tag h-4 min-w-[18px] px-1 text-[10px]",
        className,
      )}
    >
      {count > max ? `${max}+` : count}
    </span>
  );
}
